import * as React from "react"

import { cn } from "../../lib/utils"

/**
 * El interruptor de Aturno. Un solo lugar donde vive la forma y el color de un
 * toggle, igual que `Button` para los botones.
 *
 * Antes cada pantalla traía su propia copia: `.toggle-switch` / `.toggle-slider`
 * estaba declarado seis veces —dos de ellas en el mismo Dashboard.css, una
 * pisando a la otra— con un bloque que se sobrescribía a sí mismo con
 * `!important` y otro más abajo que le anulaba los transforms. Cambiar el color
 * del toggle obligaba a tocar tres hojas de estilo y a adivinar cuál ganaba.
 *
 * Por dentro es un `<input type="checkbox">` de verdad y no un `<div>` con
 * onClick: así el foco, la barra espaciadora y los lectores de pantalla
 * funcionan solos. El input está oculto con `sr-only` (no con `display: none`,
 * que lo sacaría del orden de tabulación) y las dos partes visibles —la vía y
 * el círculo— se pintan con `peer-checked`.
 *
 * La vía encendida usa `--brand-primary`, el mismo token que `aplicarBranding`
 * escribe con el color del negocio y que `index.css` le da a los checkboxes
 * nativos vía `accent-color`. Antes era un degradado naranja→violeta escrito a
 * mano: no seguía el color del negocio en su página pública, y en el panel
 * desentonaba con el resto de los controles, que son naranja liso.
 * El fallback es `--aturno-orange` para las pantallas que no son de un negocio
 * —la landing, el login, lo legal—, donde `aplicarBranding` no corre y el token
 * no está definido. En el panel y en la página pública sí corre, así que el
 * fallback no se usa.
 *
 * Igual que en `Button`: si aparece un color escrito a mano acá, es un toggle
 * que se salió del sistema.
 */
const Switch = React.forwardRef(
  ({ className, checked, onChange, disabled = false, ...props }, ref) => (
    <label
      // El dashboard apaga todos los `transform` con `!important` para matar los
      // efectos de pop-up; este atributo es el enganche que usa Dashboard.css
      // para devolverle el movimiento sólo al círculo.
      data-aturno-switch=""
      className={cn(
        "relative inline-block h-[26px] w-[50px] shrink-0 align-middle",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        className
      )}
    >
      <input
        type="checkbox"
        ref={ref}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="peer sr-only"
        {...props}
      />
      {/* La vía */}
      <span
        aria-hidden="true"
        data-slot="track"
        className="absolute inset-0 rounded-full bg-[var(--gray-300)] shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transition-colors peer-checked:bg-[var(--brand-primary,var(--aturno-orange))] peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
      />
      {/* El círculo */}
      <span
        aria-hidden="true"
        data-slot="thumb"
        className="pointer-events-none absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-transform peer-checked:translate-x-6"
      />
    </label>
  )
)
Switch.displayName = "Switch"

export { Switch }
